import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from 'api/campaign';
import * as statsApi from 'api/stats';
import { useNavigate, useLocation, matchPath, generatePath } from 'react-router-dom';
import {
  RouteList,
  AddCampaignCreativeRequest,
  CreateCampaignRequest,
  CampaignApiResponse,
  Campaign,
  UpdateCampaignRequest,
  AdminRouteList,
  UpdatePublishedCampaignRequest,
} from 'types';
import { SlidingDelayError } from './useSlidingDelay';
import { useNotifications } from './useNotifications';
import { useUsers } from './useUsers';
import { queryKeys } from 'utils';
import invariant from 'tiny-invariant';
import { useCampaignId } from './useCampaignId';
import { ApiRequestParams } from 'api/axiosInstance';

export type UseCampaign = ReturnType<typeof useCampaign>;

export const useCampaign = (isCreateCampaignFlow = false) => {
  const { userProfileQuery } = useUsers();
  const { data } = userProfileQuery;

  const campaignId = useCampaignId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setNotification } = useNotifications();
  const queryClient = useQueryClient();

  const isMyCampaignsPage = !!(
    matchPath({ path: RouteList.MyCampaigns }, pathname) || matchPath({ path: RouteList.Home }, pathname)
  );
  const isGa4ConfigPage = !!matchPath({ path: RouteList.Ga4Config }, pathname);

  const isAdminCampaignsPage =
    !!matchPath({ path: AdminRouteList.Campaigns }, pathname) ||
    !!matchPath({ path: AdminRouteList.Invoices }, pathname);

  const getCampaignQuery = useQuery({
    queryKey: queryKeys.campaign.byCampaignId(campaignId!),
    queryFn: ({ signal }) => api.fetchCampaign(campaignId!, { signal }),
    // in RightSidebar during isCreateCampaignFlow campaignId is missing,
    // after we redirected to old flow it sets from router with delay so we make sure campaignId is not empty string
    enabled: !isCreateCampaignFlow && !isGa4ConfigPage && !!campaignId,
  });

  const getCreativeTypesQuery = useQuery({
    queryKey: queryKeys.creativeTypes(),
    queryFn: api.fetchCreativeTypeList,
    enabled: !isGa4ConfigPage,
  });

  const getCampaignListQuery = useQuery({
    queryKey: queryKeys.campaign.list(),
    queryFn: api.fetchCampaignList,
    enabled: isMyCampaignsPage,
    refetchOnMount: true,
  });

  const getCampaignListStatsQuery = useQuery({
    queryKey: queryKeys.campaignListStats(),
    queryFn: statsApi.fetchCampaignListStats,
    enabled: isMyCampaignsPage && getCampaignListQuery.isFetched,
  });

  const getAllCampaignsQuery = useQuery({
    queryKey: ['allCampaigns'],
    queryFn: api.fetchAllCampaigns,
    enabled: isAdminCampaignsPage && !!data?.user?.isAdministrator,
  });

  const createCampaignMutation = useMutation({
    mutationFn: (body?: CreateCampaignRequest) => api.createCampaign(body),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: async ({ payload, ...options }: ApiRequestParams<UpdateCampaignRequest>) =>
      await api.updateCampaign(campaignId!, payload, options),
    onMutate: async (options) => {
      invariant(campaignId, 'campaignId expected');

      const queryKey = queryKeys.campaign.byCampaignId(campaignId);
      await queryClient.cancelQueries({ queryKey, exact: true }); // abort refetch
      const previousData = queryClient.getQueryData<CampaignApiResponse>(queryKey);
      invariant(previousData, 'previous campaign data expected');

      // optimistically set the local data
      queryClient.setQueryData<CampaignApiResponse>(queryKey, (prev) => {
        return {
          ...prev!,
          campaign: {
            ...prev!.campaign,
            ...(options.payload as Campaign), // override luxon to string assignment
          },
        };
      });

      return { previousData };
    },
    onError: (err, _vars, context) => {
      // roll-back to the previous state on unexpected error
      if (context?.previousData && !SlidingDelayError.isCode(err, 'RUNNING_ABORTED')) {
        queryClient.setQueryData(queryKeys.campaign.byCampaignId(campaignId!), context.previousData);
      }
    },
    onSettled: (_data, err, _vars, _context) => {
      // skip invalidating cache on abort error not to lose user's changes
      if (!SlidingDelayError.isCode(err, 'RUNNING_ABORTED')) {
        // re-fetch fresh campaign (see getCampaignQuery)
        queryClient.invalidateQueries({ queryKey: queryKeys.campaign.byCampaignId(campaignId!) });
      }
    },
  });

  const duplicateCampaignMutation = useMutation({
    mutationFn: (campaignId: number) => {
      return api.duplicateCampaign(campaignId);
    },
    onSuccess: (data) => {
      invariant(data.id, 'id expected');
      navigate(generatePath(RouteList.Campaign, { campaignId: String(data.id) }));
      setNotification('Kampagne wurde dupliziert', 'success');
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: () => api.deleteCampaign(campaignId!),
    onSuccess: () => {
      navigate(RouteList.Home);
      setNotification('Kampagne wurde gelöscht', 'success');
    },
  });

  const deleteCampaignFromListMutation = useMutation({
    mutationFn: (id: number) => api.deleteCampaign(id),
    onSuccess: () => {
      setNotification('Kampagne wurde gelöscht', 'success');
      getCampaignListQuery.refetch();
    },
  });

  const publishCampaignMutation = useMutation({
    mutationFn: () => api.publishCampaign(campaignId!),
    onSuccess: () => navigate(generatePath(RouteList.CampaignCreationSuccess, { campaignId: String(campaignId) })),
  });

  const addCampaignCreativeMutation = useMutation({
    mutationFn: (body: Omit<AddCampaignCreativeRequest, 'campaignId'>) => {
      invariant(campaignId, 'campaignId expected');
      return api.addCampaignCreative({ ...body, campaignId });
    },
    onSuccess: () => getCampaignQuery.refetch(),
  });

  const deleteCampaignCreativeMutation = useMutation({
    mutationFn: (creativeId: number) => api.deleteCampaignCreative(campaignId!, creativeId),
    onSuccess: () => getCampaignQuery.refetch(),
  });

  const updatePublishedCampaignMutation = useMutation({
    mutationFn: async ({ payload }: ApiRequestParams<UpdatePublishedCampaignRequest>) =>
      await api.updatePublishedCampaign(campaignId!, payload),
    onSuccess: () => {
      getCampaignQuery.refetch();
    },
  });

  const stopCampaignMutation = useMutation({
    mutationFn: (campaignId: number) => api.stopCampaign(campaignId!),
  });

  return {
    campaignId,
    getCampaignQuery,
    getCampaignListQuery,
    getCampaignListStatsQuery,
    createCampaignMutation,
    updateCampaignMutation,
    deleteCampaignMutation,
    deleteCampaignFromListMutation,
    duplicateCampaignMutation,
    addCampaignCreativeMutation,
    deleteCampaignCreativeMutation,
    publishCampaignMutation,
    getCreativeTypesQuery,
    getAllCampaignsQuery,
    updatePublishedCampaignMutation,
    stopCampaignMutation,
  };
};
